import { useRequestURL, useRuntimeConfig } from '#imports'
import { type ComputedRef, type Ref, computed } from 'vue'

import type { Price } from '@backmarket/http-api/src/api-models/Price'
import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import type { GetTechnicalSpecificationsResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/technical-specifications'
import type { Rating } from '@backmarket/http-api/src/api-specs-reviews/types/rating'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { pickBy } from '@backmarket/utils/object/pickBy'

import type { GetShippingResponse } from '~/api/product-page/types'
import type { DisplayedReview } from '~/scopes/reviews/reviews-display/models/reviews'

import { useProductCanonicalUrl } from './useProductCanonicalUrl'

function getSanitizedSpecs(
  technicalSpecificationsResponse: Ref<
    GetTechnicalSpecificationsResponse | undefined
  >,
) {
  // We only want to keep those, if present
  const FIELDS = ['color', 'storage', 'mpn']

  const specs = technicalSpecificationsResponse.value?.specifications?.reduce(
    (acc, curr) =>
      FIELDS.includes(curr.field) && curr.values?.[0]?.label
        ? { ...acc, [curr.field]: curr.values?.[0]?.label }
        : acc,

    { color: null, storage: null, mpn: null },
  )

  return pickBy(specs, (spec) => spec !== null)
}

export function useProductHeadJsonLd({
  description,
  isOutOfStock,
  productResponse,
  ratesResponse,
  reviewsResponse,
  technicalSpecificationsResponse,
  selectedOfferResponse,
  priceForJsonLd,
  shippingResponse,
}: {
  description: ComputedRef<string>
  isOutOfStock: Ref<boolean>
  productResponse: Ref<GetProductResponse | undefined>
  ratesResponse: Ref<Rating | undefined>
  reviewsResponse: Ref<{ results: DisplayedReview[] } | undefined>
  technicalSpecificationsResponse: Ref<
    GetTechnicalSpecificationsResponse | undefined
  >
  selectedOfferResponse: Ref<GetPickersResponse['selectedOffer']>
  /**
   * Mobile-plan subsidised price computed server-side (PPI-7740). When present it overrides the
   * raw `selectedOffer` price in the structured data so Google Merchant Center sees the discounted
   * price. Falls back to the `selectedOffer` price when absent (FF off / not gated / server error).
   */
  priceForJsonLd?: Ref<Price | undefined>
  shippingResponse: Ref<GetShippingResponse | null | undefined>
}) {
  const runtimeConfig = useRuntimeConfig()
  const { features, market } = useMarketplace()
  const requestURL = useRequestURL()
  const { canonicalUrl } = useProductCanonicalUrl({ productResponse })

  const jsonLd = computed<
    (Record<string, unknown> & { '@type': string }) | null
  >(() => {
    if (!productResponse.value || isOutOfStock.value) {
      return null
    }

    return {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      '@id': canonicalUrl.value,
      name: productResponse.value.titles.raw,
      description: description.value,
      image: productResponse.value.images?.[0]?.url ?? null,
      productID: productResponse.value.productId,
      ...getSanitizedSpecs(technicalSpecificationsResponse),
      ...insertIf(!!productResponse.value.brand, {
        brand: {
          '@type': 'Brand',
          name: productResponse.value.brand,
        },
      }),
      offers: {
        '@type': 'Offer',
        '@id': canonicalUrl.value,
        url: canonicalUrl.value,
        availability: 'http://schema.org/InStock',
        itemCondition: 'http://schema.org/RefurbishedCondition',
        ...insertIf(!isEmpty(selectedOfferResponse.value?.price), {
          priceCurrency:
            priceForJsonLd?.value?.currency ??
            selectedOfferResponse.value?.price?.currency,
          price:
            priceForJsonLd?.value?.amount ??
            selectedOfferResponse.value?.price?.amount,
        }),
        seller: {
          '@type': 'Organization',
          name: runtimeConfig.public.COMPANY_NAME,
        },
        hasMerchantReturnPolicy: {
          '@type': 'MerchantReturnPolicy',
          applicableCountry: market.countryCode,
          returnPolicyCategory:
            'https://schema.org/MerchantReturnFiniteReturnWindow',
          merchantReturnDays: features.coolingOffDays,
          returnMethod: 'https://schema.org/ReturnByMail',
          returnFees: 'https://schema.org/FreeReturn',
        },
        shippingDetails: (['free', 'express'] as const)
          .filter(
            (shippingType) =>
              !!shippingResponse.value?.[shippingType]?.seo.deliveryTime ||
              !!selectedOfferResponse.value?.shipping?.[shippingType],
          )
          .map((shippingType) => {
            return {
              '@type': 'OfferShippingDetails',
              shippingRate: {
                '@type': 'MonetaryAmount',
                value:
                  selectedOfferResponse.value?.shipping?.[shippingType]?.price
                    ?.amount || 0,
                currency: market.defaultCurrency,
              },
              shippingDestination: {
                '@type': 'DefinedRegion',
                addressCountry: market.countryCode,
              },
              deliveryTime:
                shippingResponse.value?.[shippingType]?.seo.deliveryTime ??
                selectedOfferResponse.value?.shipping?.[shippingType]?.seo
                  .deliveryTime,
            }
          }),
      },
      ...insertIf(
        !isEmpty(ratesResponse.value) && ratesResponse.value?.count > 0,
        {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: ratesResponse.value?.averageRate,
            reviewCount: ratesResponse.value?.count,
            bestRating: 5,
            worstRating: 1,
          },
        },
      ),
      ...insertIf(!!reviewsResponse.value?.results.length, {
        review: reviewsResponse.value?.results.map((review) => ({
          '@type': 'Review',
          '@id': `review-${review.id}`,
          url: `${requestURL.href}#review-${review.id}`,
          reviewRating: {
            '@type': 'Rating',
            ratingValue: review.averageRate,
            bestRating: 5,
            worstRating: 1,
          },
          author: {
            '@type': 'Person',
            name: `${review.customer.firstName} ${review.customer.lastName}`,
          },
          reviewBody: review.comment,
          datePublished: review.createdAt,
          inLanguage: `${review.languageCode.toLowerCase()}-${review.countryCode.toUpperCase()}`,
          publisher: {
            '@type': 'Organization',
            name: runtimeConfig.public.COMPANY_NAME,
            url: requestURL.origin,
          },
          upvoteCount: review.voteCount ?? 0,
        })),
      }),
    }
  })

  return { jsonLd }
}
